/**
 * docUrl.js — Document URL utility
 *
 * Converts a stored file_path (from the DB) into a full, openable URL.
 *
 * Handles three cases:
 *   1. Cloudinary URL  → already full URL → return as-is
 *   2. Local path      → prefix with backend base URL
 *   3. Already full URL (http/https) → return as-is
 *
 * Usage:
 *   import { getDocUrl } from '../../utils/docUrl';
 *   window.open(getDocUrl(doc.file_path), '_blank');
 *   <a href={getDocUrl(doc.file_path)} target="_blank">View</a>
 */

// Backend base URL — strips /api/v1 suffix
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1')
    .replace(/\/api\/v1\/?$/, '');

/**
 * Returns a full URL to open/download a document.
 * @param {string} filePath - value from doc.file_path in DB
 * @returns {string} full URL
 */
export const getDocUrl = (filePath) => {
    if (!filePath) return '#';
    // Already a full URL (Cloudinary or other CDN)
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
    }
    // Local path — may start with / or not
    const clean = filePath.startsWith('/') ? filePath : `/${filePath}`;
    return `${API_BASE}${clean}`;
};

/**
 * Opens a document in a new browser tab.
 * @param {string} filePath
 */
export const openDoc = (filePath) => {
    const url = getDocUrl(filePath);
    if (url === '#') return;
    window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Triggers a file download.
 * @param {string} filePath
 * @param {string} fileName - suggested download filename
 */
export const downloadDoc = (filePath, fileName) => {
    const url = getDocUrl(filePath);
    if (url === '#') return;
    const a = document.createElement('a');
    a.href     = url;
    a.download = fileName || 'document';
    a.target   = '_blank';
    a.rel      = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};
